import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBrandReport,
  getDomainReport,
  getUrlReport,
  getBrandTrend,
  getCostSummary,
  getTopicHeatmap,
  listVisibilityChats,
  getVisibilityChat,
  listVisibilityBrands,
  listVisibilityEngines,
  upsertVisibilityBrand,
  listVisibilityTags,
  getProbeTags,
  setProbeTags,
  seedVisibility,
} from '../api/visibility';

export function useBrandReport(from: string, to: string, opts?: { engineIds?: string; order_by?: string }) {
  return useQuery({
    queryKey: ['visibility', 'report', 'brand', from, to, opts?.engineIds, opts?.order_by],
    queryFn: () => getBrandReport({ from, to, ...(opts?.engineIds ? { engineIds: opts.engineIds } : {}), ...(opts?.order_by ? { order_by: opts.order_by } : {}) }),
  });
}

export function useDomainReport(from: string, to: string, opts?: { gap?: boolean; domain_class?: string }) {
  return useQuery({
    queryKey: ['visibility', 'report', 'domain', from, to, opts?.gap, opts?.domain_class],
    queryFn: () => getDomainReport({ from, to, ...(opts?.gap ? { gap: opts.gap } : {}), ...(opts?.domain_class ? { domain_class: opts.domain_class } : {}) }),
  });
}

export function useUrlReport(from: string, to: string, opts?: { gap?: boolean; url_classification?: string }) {
  return useQuery({
    queryKey: ['visibility', 'report', 'url', from, to, opts?.gap, opts?.url_classification],
    queryFn: () => getUrlReport({ from, to, ...(opts?.gap ? { gap: opts.gap } : {}), ...(opts?.url_classification ? { url_classification: opts.url_classification } : {}) }),
  });
}

export function useVisibilityChats(from: string, to: string, opts?: { probeId?: string; provider?: string; cited?: boolean }) {
  return useQuery({
    queryKey: ['visibility', 'chats', from, to, opts?.probeId, opts?.provider, opts?.cited],
    queryFn: () => listVisibilityChats({ from, to, ...(opts?.probeId ? { probeId: opts.probeId } : {}), ...(opts?.provider ? { provider: opts.provider } : {}), ...(typeof opts?.cited === 'boolean' ? { cited: opts.cited } : {}) }),
  });
}

export function useVisibilityChat(id: string | null) {
  return useQuery({
    queryKey: ['visibility', 'chat', id],
    queryFn: () => getVisibilityChat(id!),
    enabled: !!id,
  });
}

export function useBrandTrend(from: string, to: string) {
  return useQuery({
    queryKey: ['visibility', 'brand-trend', from, to],
    queryFn: () => getBrandTrend({ from, to }),
  });
}

export function useCostSummary(from: string, to: string) {
  return useQuery({
    queryKey: ['visibility', 'costs', from, to],
    queryFn: () => getCostSummary({ from, to }),
  });
}

export function useTopicHeatmap(from: string, to: string) {
  return useQuery({
    queryKey: ['visibility', 'topic-heatmap', from, to],
    queryFn: () => getTopicHeatmap({ from, to }),
  });
}

export function useVisibilityBrands() {
  return useQuery({ queryKey: ['visibility', 'brands'], queryFn: listVisibilityBrands });
}

export function useVisibilityEngines() {
  return useQuery({ queryKey: ['visibility', 'engines'], queryFn: listVisibilityEngines });
}

export function useVisibilityTags() {
  return useQuery({ queryKey: ['visibility', 'tags'], queryFn: listVisibilityTags });
}

export function useProbeTags(probeId: string | null) {
  return useQuery({
    queryKey: ['visibility', 'probe-tags', probeId],
    queryFn: () => getProbeTags(probeId!),
    enabled: !!probeId,
  });
}

export function useSetProbeTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ probeId, tagIds }: { probeId: string; tagIds: string[] }) => setProbeTags(probeId, tagIds),
    onSuccess: (_, { probeId }) => {
      qc.invalidateQueries({ queryKey: ['visibility', 'probe-tags', probeId] });
      qc.invalidateQueries({ queryKey: ['measurement', 'recommendations'] });
    },
  });
}

export function useUpsertVisibilityBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: upsertVisibilityBrand,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visibility', 'brands'] });
      qc.invalidateQueries({ queryKey: ['visibility', 'report', 'brand'] });
      qc.invalidateQueries({ queryKey: ['visibility', 'brand-trend'] });
    },
  });
}

export function useSeedVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: seedVisibility,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visibility'] });
      qc.invalidateQueries({ queryKey: ['measurement'] });
    },
  });
}
